import { connect } from "react-redux";
import { withNamespaces } from "react-i18next";
import React, { useState, useEffect, memo, useMemo, useCallback } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { successNotification, errorNotification } from "src/store/actions.js";
import { toast } from "react-toastify";
import { Button, Col, Row, UncontrolledTooltip } from "reactstrap";
import { HiChevronDoubleUp, HiChevronDoubleDown, HiMenuAlt4 } from 'react-icons/hi';

import DeleteTaskModal from "src/modules/data-protection/components/RisksAndTasks/tasks/delete-modal";
import CreateTaskModal from "src/modules/data-protection/components/RisksAndTasks/tasks/new";
import OverviewHeader from "../../../overview-header";
import DateUtils from "src/services/utils/DateUtils";
import { ControlsFrameworks, TasksStatuses } from "src/modules/data-protection/constants/Common";
import {
    EMPTY_LIST,
    INTERNATIONAL_DATE_FORMAT
} from "src/common/constants";

import TaskManagerService from "src/modules/data-protection/apis/TaskManagerService";
import OtherAssetsTaskManagerService from "src/modules/data-protection/apis/OtherAssetsTaskManagerService";
import { withRouter, useLocation } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from "react-bootstrap-table2-overlay";
import paginationFactory, {
    PaginationProvider,
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
} from "react-bootstrap-table2-paginator";
import { formatTaskAndRiskId } from "src/modules/data-protection/constants/Common";
import FilteringComponent from "./components/filtering-component";
import ComplianceFrameworkSettingsService from "src/modules/data-protection/apis/ComplianceFrameworkSettingsService";
import { formatControlNumber } from "src/modules/data-protection/helpers/Controls";

const TaskManager = (props) => {
    const {
        t,
        assetId,
        onTaskSelected
    } = props;

    const ismsControlsOptions = {
        otherAsset: assetId
    }

    const location = useLocation();

    const [tableFilters, setTableFilters] = useState({
        pageIndex: 1,
        pageSize: 25,
        status: null,
        priority: null,
        owner: null,
    });
    const [deleteTaskId, setDeleteTaskId] = useState(null);

    const [createTaskModalStatus, setCreateTaskModalStatus] = useState(false);
    const [deleteTaskModalStatus, setDeleteTaskModalStatus] = useState(false);

    const dateUtils = new DateUtils();

    const handleFetchTasksQuery = useQuery({
        queryKey: ['data-protection-other-assets-tasks', assetId, tableFilters],
        queryFn: async () => {
            const service = OtherAssetsTaskManagerService.getInstance();

            return await service.fetchTasks(assetId, tableFilters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching tasks.'), {
                type: 'error'
            });
        },
    });

    const ismsAccessibleControlsQuery = useQuery({
        queryKey: [
            'data-protection-other-assets-isms-accessible-controls',
            ismsControlsOptions
        ],
        queryFn: async () => {
            const service = ComplianceFrameworkSettingsService.getInstance();

            return await service.fetchAccessibleControls({
                ...ismsControlsOptions
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t('An error occurred while retrieving the controls list.'), {
                type: 'error'
            });
        }
    });

    const handleFetchTasksOwners = useQuery({
        queryKey: ['data-protection-other-assets-task-owners', assetId],
        queryFn: async () => {
            const service = TaskManagerService.getInstance();

            return await service.fetchOwners(assetId);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            toast(t('An error occurred while fetching owners.'), {
                type: 'error',
            });
        },
    });

    const handleDeleteTaskMutation = useMutation({
        mutationFn: async (taskId) => {
            const service = TaskManagerService.getInstance();

            return await service.deleteTask(assetId, taskId);
        },
        onSuccess: () => {
            setDeleteTaskModalStatus(false);

            handleFetchTasksQuery.refetch();

            toast(t("Task deleted successfully."), {
                type: "success",
            });
        },
        onError: () => {
            toast(t("Failed to delete task."), {
                type: "error",
            });
        }
    });

    const priorities = useMemo(() => {
        return [
            {
                value: 'low',
                baseLabel: 'Low',
                label: (
                    <>
                        <HiChevronDoubleDown className='text-info' />
                        <span>{t('Low')}</span>
                    </>
                ),
            },
            {
                value: 'medium',
                baseLabel: 'Medium',
                label: (
                    <>
                        <HiMenuAlt4 className='text-success' />
                        <span>{t('Medium')}</span>
                    </>
                ),
            },
            {
                value: 'high',
                baseLabel: 'High',
                label: (
                    <>
                        <HiChevronDoubleUp className='text-danger' />
                        <span>{t('High')}</span>
                    </>
                ),
            },
        ];
    }, [t]);

    const statuses = useMemo(() => {
        return [
            { value: 'NEW', label: t('New') },
            { value: 'IN_PROGRESS', label: t('In Progress') },
            { value: 'REVIEW', label: t('Review') },
            { value: 'CLOSED', label: t('Closed') },
            { value: 'DELETED', label: t('Deleted') },
        ];
    }, [t]);

    const owners = useMemo(() => {
        return (handleFetchTasksOwners.data || []).map((item) => {
            return {
                value: item.id,
                label: `${item.first_name} ${item.last_name}`
            };
        })
    }, [
        handleFetchTasksOwners.data
    ]);

    const taskSelectedToShow = (id) => {
        props.history.push({
            search: '?tab=task-management&id=' + id
        })

        onTaskSelected && onTaskSelected({
            taskId: id
        });
    }

    useEffect(() => {
        const taskId = new URLSearchParams(location.search).get("taskId");

        if (taskId && owners.length > 0) {
            taskSelectedToShow(taskId);
        }
    }, [
        owners, 
        location.search
    ]);

    const renderModals = () => {
        return (
            <React.Fragment>
                <CreateTaskModal
                    isOpen={createTaskModalStatus}
                    closeModal={() => setCreateTaskModalStatus(false)}
                    owners={owners}
                    initialTaskStatus={{ value: 'NEW', label: t('New') }}
                    priorities={priorities}
                    successCreation={() => handleFetchTasksQuery.refetch()}
                    creationRequestPayload={{
                        otherAsset: assetId
                    }}
                    ismsControlsOptions={ismsControlsOptions}
                />

                <DeleteTaskModal
                    isOpen={deleteTaskModalStatus}
                    taskId={deleteTaskId}
                    close={() => {
                        setDeleteTaskId(null);
                        setDeleteTaskModalStatus(false);
                    }}
                    acceptDeletion={(id) => {
                        handleDeleteTaskMutation.mutate(id);
                    }}
                />
            </React.Fragment>
        );
    }

    const handleRenderReportStatus = (statusId) => {
        const taskStatus = TasksStatuses[statusId];

        return (
            <div className='dt-task-status text-dark' style={{
                backgroundColor: `${taskStatus.color}`
            }}>
                {t(taskStatus.title)}
            </div>
        )
    };

    const {
        accessible_frameworks = []
    } = ismsAccessibleControlsQuery.data || {};

    const controlsColumns = accessible_frameworks?.map((accessibleFramework, index) => {
        const controleInfos = ControlsFrameworks.find((item) => item.key === accessibleFramework);

        if (!controleInfos) return undefined;

        return {
            key: (9 + index),
            dataField: "controls",
            text: props.t(controleInfos.title),
            style: {
                width: '160px'
            },
            formatter: (cellContent) => {
                return (
                    <div>
                        {(cellContent[accessibleFramework] || []).map((c) => formatControlNumber(c.control_number, accessibleFramework)).join(', ')}
                    </div>
                )
            },
            sort: false
        }
    });

    const tableColumns = [
        {
            key: 1,
            dataField: "id",
            text: props.t("Task ID"),
            sort: true,
            style: {
                width: '120px'
            },
            formatter: (cellContent, row) => {
                return (
                    <div>
                        <div
                            className="dt-task-id"
                            style={{
                                cursor: !row.deleted_at ? "pointer" : "default",
                            }}
                            onClick={() => !row.deleted_at && taskSelectedToShow(row.id)}
                        >
                            {formatTaskAndRiskId(
                                'task',
                                row?.asset_id,
                                row?.risk_secondary_id ?? row.secondary_id,
                                row?.is_mitigation,
                                row?.questionnaire,
                                row.secondary_id
                            )}
                        </div> </div>
                );
            },
        },
        {
            key: 2,
            dataField: "name",
            text: props.t("Task"),
            sort: true,
            style: {
                width: '140px'
            },
            formatter: (cellContent, {
                id,
                is_recurring,
                recurrence_secondary_id,
                deleted_at
            }) => {
                return (
                    <div className="d-flex align-items-center">
                        {is_recurring && recurrence_secondary_id && (
                            <>
                                <i
                                    className="ri-repeat-2-line text-info me-1 font-size-18 align-middle"
                                    id={`recurring-task-${id}-tooltip`}
                                    style={{
                                        cursor: 'pointer'
                                    }}
                                />
                                <UncontrolledTooltip
                                    placement="top"
                                    target={`recurring-task-${id}-tooltip`}
                                >
                                    {t('Recurring task')}
                                </UncontrolledTooltip>
                            </>
                        )}
                        <span
                            className={`${deleted_at ? "" : "hoverable"}`}
                            style={{
                                cursor: !deleted_at ? "pointer" : "default",
                            }}
                            onClick={() => !deleted_at && taskSelectedToShow(id)}
                        >
                            {cellContent}{recurrence_secondary_id ? `-${recurrence_secondary_id}` : ''}
                        </span>
                    </div>
                );
            }
        },
        {
            key: 3,
            dataField: "createdAt",
            text: props.t("Creation Date"),
            sort: true,
            style: {
                width: '120px'
            },
            formatter: (_cellContent, row) => {
                return (
                    <div>
                        <div className="dt-task-creation-date">
                            {dateUtils.convertTimeStampToDate(
                                row.created_at,
                                INTERNATIONAL_DATE_FORMAT
                            )}
                        </div></div>
                );
            },
        },
        {
            key: 4,
            dataField: "endedAt",
            text: props.t("Deadline"),
            sort: true,
            style: {
                width: '120px'
            },
            formatter: (_cellContent, row) => {
                return (
                    <div>
                        <div className="dt-task-deadline-date">
                            {dateUtils.convertTimeStampToDate(
                                row.ended_at,
                                INTERNATIONAL_DATE_FORMAT
                            )}
                        </div></div>
                );
            },
        },
        {
            key: 5,
            dataField: "manager",
            text: props.t("Task Manager"),
            sort: true,
            style: {
                width: '180px'
            },
            formatter: (cellContent, row) => {
                return (
                    <div className="d-flex align-items-center">
                        <span className="dt-list-col-bullet">{row.manager[0]}</span>{" "}
                        <span className="dt-list-col-bullet-text">{row.manager}</span>
                    </div>
                );
            },
        },
        {
            key: 6,
            dataField: "analyst",
            text: props.t("Task Owner"),
            sort: true,
            style: {
                width: '180px'
            },
            formatter: (cellContent, row) => {
                return (
                    <div className="d-flex align-items-center">
                        <span className="dt-list-col-bullet">
                            {row?.analyst?.full_name[0]}
                        </span>{" "}
                        <span className="dt-list-col-bullet-text">
                            {row?.analyst?.full_name}
                        </span>
                    </div>
                );
            },
        },
        {
            key: 7,
            dataField: "priority",
            text: props.t("Priority"),
            sort: true,
            style: {
                width: '100px'
            },
            formatter: (cellContent, row) => {
                return (
                    <div className="dt-task-priority">
                        {row.priority === "high".toLowerCase() ? (
                            <HiChevronDoubleUp className="text-danger" />
                        ) : row.priority.toLowerCase() === "medium" ? (
                            <HiMenuAlt4 className="text-success" />
                        ) : (
                            <HiChevronDoubleDown className="text-info" />
                        )}
                        <span style={{ marginLeft: "4px" }}>{t(row.priority)}</span>
                    </div>
                );
            },
        },
        {
            key: 8,
            dataField: "status",
            text: props.t("Status"),
            sort: true,
            style: {
                width: '100px'
            },
            formatter: (cellContent, row) => {
                return <div>{handleRenderReportStatus(row.status)}</div>;
            },
        },
        ...controlsColumns,
        {
            key: 12,
            dataField: "deletedAt",
            text: props.t("Deletion date"),
            sort: true,
            style: {
                width: '120px'
            },
            formatter: (_cellContent, row) => {
                return (
                    <div>
                        {row.deleted_at
                            ? dateUtils.convertTimeStampToDate(
                                row.deleted_at,
                                INTERNATIONAL_DATE_FORMAT
                            )
                            : null}
                    </div>
                );
            },
        },
        {
            key: 13,
            dataField: "actions",
            text: props.t("Action"),
            style: {
                width: '120px'
            },
            formatter: (cellContent, row) => {
                return (
                    <div>
                        {!row.deleted_at ? (
                            <div
                                style={{
                                    fontSize: 20,
                                    display: "block",
                                    cursor: "pointer",
                                }}
                                onClick={() => {
                                    setDeleteTaskId(row.id);
                                    setDeleteTaskModalStatus(true);
                                }}
                            >
                                <i className="ri-delete-bin-line text-danger"></i>
                            </div>
                        ) : (
                            <div
                                style={{
                                    fontWeight: 300,
                                    textAlign: "center",
                                    display: "block",
                                    color: "red",
                                }}
                            >
                                {t("Deleted")}
                            </div>
                        )}
                    </div>
                );
            },
        }
    ];

    const NoDataIndication = () =>
        handleFetchTasksQuery.isFetched &&
            !handleFetchTasksQuery.length ? (
            <div className="alert m-0" role="alert">
                <p
                    style={{
                        textAlign: "center",
                        marginBottom: 0,
                    }}
                >
                    {props.t(EMPTY_LIST)}
                </p>
            </div>
        ) : (
            <></>
        );

    const handleTableChange = (type, {
        page,
        sizePerPage,

        sortField,
        sortOrder
    }) => {
        switch (type) {
            case "pagination":
                setTableFilters({
                    ...tableFilters,
                    pageIndex: page,
                    pageSize: sizePerPage,
                });
                break;
            case 'sort':
                setTableFilters({
                    ...tableFilters,
                    sortField: sortField,
                    sortOrder: sortOrder.toUpperCase()
                })
                break;
            default:
                return false;
        }
    };

    const handleFilterOnChange = useCallback((newFilters) => {
        setTableFilters((currentStatue) => {
            return {
                ...currentStatue,
                ...newFilters
            }
        });
    }, []);

    return (
        <div className="p-4" style={{ marginBottom: "300px" }}>
            {renderModals()}
            <div className="mb-4">
                <OverviewHeader
                    assetId={assetId}
                />
            </div>

            <PaginationProvider
                pagination={paginationFactory({
                    custom: true,
                    page: tableFilters.pageIndex,
                    sizePerPage: tableFilters.pageSize,
                    totalSize: handleFetchTasksQuery.data?.itemsCount || 0,
                    withFirstAndLast: false,
                    alwaysShowAllBtns: true,
                    prePageText: (
                        <span>
                            <i className="ri-arrow-left-s-line"></i> {props.t("Back")}
                        </span>
                    ),
                    nextPageText: (
                        <span>
                            {props.t("Next")} <i className="ri-arrow-right-s-line"></i>
                        </span>
                    ),
                    prePageTitle: props.t("Pre page"),
                    firstPageTitle: props.t("Next page"),
                    showTotal: true,
                    hideSizePerPage: false,
                    sizePerPageList: [
                        {
                            text: "25",
                            value: 25,
                        },
                        {
                            text: "50",
                            value: 50,
                        },
                    ],
                })}
            >
                {({ paginationProps, paginationTableProps }) => {
                    return (
                        <React.Fragment>
                            <Row className="mb-5 mt-3">
                                <Col sm="12">
                                    <div className="d-flex gap-2 justify-content-end">
                                        <FilteringComponent 
                                            onChange={handleFilterOnChange} 
                                            owners={handleFetchTasksOwners}
                                            statuses={statuses}
                                            priorities={priorities}
                                        />

                                        <Button
                                            color="primary"
                                            style={{
                                                display: "block",
                                                height: "max-content"
                                            }}
                                            onClick={() => setCreateTaskModalStatus(true)}
                                            outline
                                        >
                                            {t("Create Task")}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm="12">
                                    <BootstrapTable
                                        remote={{
                                            pagination: true,
                                            filter: false,
                                            sort: true,
                                            cellEdit: false,
                                            search: false,
                                        }}
                                        loading={
                                            handleFetchTasksQuery.isFetching ||
                                            handleFetchTasksQuery.isLoading ||
                                            handleFetchTasksOwners.isLoading ||
                                            handleFetchTasksOwners.isFetching
                                        }
                                        overlay={overlayFactory({
                                            spinner: (
                                                <Spinner animation="border" variant="primary" size="md" />
                                            ),
                                            text: "Loading...",
                                        })}
                                        onTableChange={handleTableChange}
                                        defaultSorted={[]}
                                        keyField={"id"}
                                        responsive
                                        bordered={false}
                                        data={handleFetchTasksQuery.data?.tasks || []}
                                        striped={true}
                                        columns={tableColumns}
                                        wrapperClasses="table-responsive"
                                        classes={"table tpdd-table incident-table"}
                                        headerWrapperClasses={"thead-light"}
                                        style={{
                                            overflowX: "auto",
                                        }}
                                        noDataIndication={() => <NoDataIndication />}
                                        {...paginationTableProps}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col sm="12" md="6">
                                    <div className="tpdd-pagination-style-1">
                                        <PaginationListStandalone {...paginationProps} />

                                        <SizePerPageDropdownStandalone {...paginationProps} />
                                    </div>
                                </Col>
                            </Row>
                        </React.Fragment>
                    )
                }}
            </PaginationProvider>
        </div>
    );
};

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    const { Organization } = state;
    return {
        user: state.Login.user,
        token,
        organization: Organization
    };
};

export default withNamespaces()(
    withRouter(connect(
        mapStatetoProps,
        {
            successNotification,
            errorNotification
        }
    )(memo(TaskManager)))
);